export const trainingHero = {
  title: '実践トレーニング',
  titleEn: 'PRACTICAL TRAINING',
  lead: '当アカデミーの学びの中心は、現場を想定した実技とロールプレイングです。講義で得た知識をすぐに体で覚え、本物の環境で即戦力を養います。',
};

export const basicTraining = {
  title: '基礎トレーニング',
  titleEn: 'Basic Training',
  description: 'まずは理論で土台を作り、すぐに実技へ移ります。たとえばテーブルセッティングを学んだその日に、実際にフォーマルディナーを設定してみる——学びを体で覚える設計です。講義と実技の間を最短にすることで、知識が頭で終わらず身体に染み込むよう工夫しています。',
  features: [
    {
      title: '講義と実技の直結',
      description: '座学の直後に実技演習を行うことで、知識が定着しやすく、疑問点をその場で解消できます。',
    },
    {
      title: '少人数制の徹底',
      description: '1クラス最大6名の完全少人数制。講師の目が全員に行き届き、一人ひとりの進度に合わせた指導が可能です。',
    },
    {
      title: '反復練習の重視',
      description: '基本の所作は何度でも繰り返します。正しい姿勢・歩き方・手の動きが自然にできるまで、根気よく指導します。',
    },
  ],
  imageSeq: 'training-basic',
};

export const rolePlaying = {
  title: 'ロールプレイング実習',
  titleEn: 'Role-Playing Practice',
  description: '現場では、想定外のことが起こります。だからこそ、日常の家事サービスから晩餐会の運営、緊急時の対応まで、幅広い場面を想定して繰り返し練習します。実際の環境に近い設定で、臨機応変な対応力を養います。',
  scenarios: [
    {
      title: '日常サービス',
      titleEn: 'Daily Service',
      level: '初級',
      description: '毎日の家事管理や来客応対を想定したシナリオ。ティーサービス、簡単なテーブルセッティング、基本的な所作の反復練習から始めます。',
      icon: 'ri-home-4-line',
      imageSeq: 'roleplay-daily',
    },
    {
      title: 'フォーマル晩餐会',
      titleEn: 'Formal Dinner',
      level: '中級',
      description: '複数名のゲストをもてなすフルコースディナーの運営。テーブルコーディネートからワインサービス、ゲストごとの対応までを総合的に実践します。',
      icon: 'ri-goblet-line',
      imageSeq: 'roleplay-formal',
    },
    {
      title: '緊急時対応',
      titleEn: 'Crisis Management',
      level: '上級',
      description: '予期せぬトラブルや緊急事態を想定した高度なシナリオ。冷静な判断力と、周囲を落ち着かせる所作を訓練します。マスターコースで扱います。',
      icon: 'ri-alert-line',
      imageSeq: 'roleplay-crisis',
    },
  ],
  features: [
    {
      label: '実際の環境に近い設定',
      text: '邸宅や高級ホテルを模したスタジオで、現実に即した場面を忠実に再現します。',
    },
    {
      label: '段階的に難易度を上げる',
      text: '初級の基本所作から、上級の複合的な危機対応まで、着実にステップアップします。',
    },
    {
      label: '即時フィードバック',
      text: '実習の直後に講師が具体的な講評を行い、改善点をその場で明確にします。',
    },
  ],
};

export const feedbackSystem = {
  title: 'フィードバックの仕組み',
  titleEn: 'Feedback System',
  description: '実習の後には必ず講評の時間を設けています。講師が各受講生の所作・判断・コミュニケーションを細かく観察し、良かった点と改善点を具体的にフィードバック。この講評→改善→再挑戦の循環が、確かな成長を支えます。',
  steps: [
    {
      step: '01',
      title: '実習観察',
      titleEn: 'Observation',
      description: '講師がロールプレイング中の所作・判断・言葉遣いを詳細に記録します。',
      icon: 'ri-eye-line',
    },
    {
      step: '02',
      title: '個別講評',
      titleEn: 'Review',
      description: '実習直後に一人ひとりへ口頭でフィードバック。良かった点をまず伝え、その上で改善点を具体的に指導します。',
      icon: 'ri-chat-1-line',
    },
    {
      step: '03',
      title: '改善実践',
      titleEn: 'Improvement',
      description: '指摘を受けた点を意識して、同じシナリオにもう一度挑戦。できなかったことができるようになる達成感を味わいます。',
      icon: 'ri-loop-left-line',
    },
    {
      step: '04',
      title: '成長記録',
      titleEn: 'Progress Record',
      description: '各回の評価を蓄積し、受講生自身が成長の軌跡を振り返れるようにしています。',
      icon: 'ri-file-chart-line',
    },
  ],
};

export const safetyEquipment = {
  title: '安全・実物主義',
  titleEn: 'Safety & Authenticity',
  description: '当アカデミーの実技指導では、実物の什器・道具を使用します。模造品ではなく本物に触れることで、質感・重さ・扱い方を身体で覚えます。また、受講生の皆様には初日から白手袋を着用いただき、プロフェッショナルとしての所作の基礎を身につけます。',
  items: [
    {
      title: '実物の什器・道具',
      description: 'クリスタルガラス、シルバーカトラリー、高級陶磁器など、実際の邸宅で使用される本物の什器を用いて訓練します。',
      icon: 'ri-tools-line',
    },
    {
      title: '白手袋の着用',
      description: '初日からプロ仕様の白手袋を着用。手の動き一つひとつに品格が宿るよう、基本の所作から徹底します。',
      icon: 'ri-hand-sanitizer-line',
    },
    {
      title: '安全への配慮',
      description: 'すべての実技は経験豊富な講師の監督のもとで行います。怪我や破損のリスクを最小限に抑え、安心して学べる環境を整えています。',
      icon: 'ri-shield-check-line',
    },
  ],
};

export const trainingFaqs = [
  {
    question: '実技はどのくらいありますか？',
    answer: '講義で学んだ内容を必ず実技で試す設計です。テーブルセッティングやサービスの実習、現場を想定したロールプレイングが学びの中心です。',
  },
  {
    question: 'どんな場面を想定しますか？',
    answer: '日常の家事サービスから、フォーマルな晩餐会の運営、緊急時の対応まで幅広いシナリオを用意しています。',
  },
  {
    question: '実技だけ体験できますか？',
    answer: 'はい。1日完結の体験講座で、所作やティーサービスを実際に体験いただけます。',
  },
];

export const trainingRelatedPages = [
  {
    title: '使用教材',
    description: '協会監修の教科書全15冊',
    href: '/materials',
  },
  {
    title: '習得スキル一覧',
    description: '身につく5つのスキル領域',
    href: '/skills',
  },
  {
    title: 'カリキュラム概要',
    description: '学びの全体像と進め方',
    href: '/curriculum',
  },
  {
    title: '体験講座',
    description: '1日で執事の所作を体験',
    href: '/seminar',
  },
];